import { api } from './apiClient';

/**
 * Serial-number tracking client (migration 062): master + status ledger +
 * genealogy. Under /serial-numbers.
 */
export const SERIAL_STATUSES = [
  'AVAILABLE', 'RESERVED', 'ISSUED', 'INSTALLED', 'RETURNED', 'UNDER_REPAIR', 'SCRAPPED',
] as const;
export type SerialStatus = (typeof SERIAL_STATUSES)[number];

export interface SerialRow {
  id: number;
  product_id: number;
  serial_number: string;
  product_code?: string;
  product_name?: string;
  batch_number?: string | null;
  warehouse_code?: string | null;
  warehouse_name?: string | null;
  status: SerialStatus;
  customer_name?: string | null;
  project_name?: string | null;
  grn_number?: string | null;
  warranty_start?: string | null;
  warranty_end?: string | null;
  notes?: string | null;
  created_at?: string;
}

export interface SerialFilters {
  productId?: number;
  warehouseId?: number;
  status?: SerialStatus;
  customerId?: number;
  projectId?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export const serialNumberService = {
  list: (filters: SerialFilters = {}): Promise<{ serials: SerialRow[]; pagination: any }> =>
    api.get('/serial-numbers', { params: filters }),
  stats: (): Promise<{ byStatus: Record<string, number>; total: number; warrantyExpiringSoonCount: number }> =>
    api.get('/serial-numbers/stats'),
  get: (id: number): Promise<{ serial: SerialRow }> => api.get(`/serial-numbers/${id}`),
  create: (data: any): Promise<{ serial: SerialRow }> => api.post('/serial-numbers', data),
  bulkCreate: (data: {
    productId: number;
    warehouseId?: number;
    batchId?: number;
    grnId?: number;
    serialNumbers: string[];
  }): Promise<{ createdCount: number; skippedCount: number; created: any[]; skipped: any[] }> =>
    api.post('/serial-numbers/bulk', data),
  update: (id: number, data: any): Promise<{ serial: SerialRow }> => api.put(`/serial-numbers/${id}`, data),
  setStatus: (id: number, status: SerialStatus, extra?: any): Promise<{ serial: SerialRow }> =>
    api.patch(`/serial-numbers/${id}/status`, { status, ...(extra || {}) }),
  remove: (id: number): Promise<void> => api.delete(`/serial-numbers/${id}`).then(() => undefined),
  genealogy: (id: number): Promise<{ serial: any; stages: Array<{ stage: string; present: boolean; label: string; link?: string | null }> }> =>
    api.get(`/serial-numbers/${id}/genealogy`),
};

export default serialNumberService;
